(() => {
  const ROOT_SELECTOR = '[data-tpc-product]';
  const HIGHLIGHT_DURATION_MS = 5200;
  const highlightTimers = new WeakMap();
  const focusFadeTimers = new WeakMap();
  const lastGuideTimes = new WeakMap();

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const getTopViewportInset = () => {
    const margin = window.innerWidth <= 749 ? 18 : 24;
    const candidates = new Set();
    const selector = [
      'sticky-header',
      'header',
      '#shopify-section-header',
      '.header-wrapper',
      '[data-sticky-header]',
      '[class*="announcement"]',
      '[id*="announcement"]'
    ].join(',');

    document.querySelectorAll(selector).forEach((element) => candidates.add(element));

    // Detecta también wrappers internos del encabezado que cubren la parte superior,
    // aunque el tema no use nombres de clase reconocibles.
    [12, Math.round(window.innerWidth / 2), Math.max(12, window.innerWidth - 12)].forEach((x) => {
      let element = document.elementFromPoint(x, 1);
      while (element && element !== document.documentElement) {
        candidates.add(element);
        element = element.parentElement;
      }
    });

    let coveredTop = 0;
    candidates.forEach((element) => {
      const style = window.getComputedStyle(element);
      const rect = element.getBoundingClientRect();
      const overlaysViewport = style.position === 'fixed' || style.position === 'sticky';
      const isVisible = style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity || 1) > 0;
      const spansUsefulWidth = rect.width >= Math.min(window.innerWidth * 0.45, 420);

      if (
        overlaysViewport
        && isVisible
        && spansUsefulWidth
        && rect.height > 0
        && rect.bottom > 0
        && rect.top <= 4
      ) {
        coveredTop = Math.max(coveredTop, rect.bottom);
      }
    });

    return Math.max(0, coveredTop) + margin;
  };

  const scrollActivePurchasePanelToTop = (root) => {
    const panel = root.querySelector('[data-tpc-purchase-panel]:not([hidden])');
    if (!panel) return;

    const targetTop = panel.getBoundingClientRect().top + window.scrollY - getTopViewportInset();
    window.scrollTo({
      top: Math.max(0, targetTop),
      behavior: prefersReducedMotion() ? 'auto' : 'smooth'
    });
  };

  const activateFocusFade = (root) => {
    const previousTimer = focusFadeTimers.get(root);
    if (previousTimer) window.clearTimeout(previousTimer);
    focusFadeTimers.delete(root);

    root.classList.remove('is-choice-focus-active');
    void root.offsetWidth;
    root.classList.add('is-choice-focus-active');

    const duration = prefersReducedMotion() ? 1800 : HIGHLIGHT_DURATION_MS;
    const timer = window.setTimeout(() => {
      root.classList.remove('is-choice-focus-active');
      focusFadeTimers.delete(root);
    }, duration);

    focusFadeTimers.set(root, timer);
  };

  const highlightActivePurchasePanel = (root) => {
    const panel = root.querySelector('[data-tpc-purchase-panel]:not([hidden])');
    if (!panel) return;

    root.querySelectorAll('[data-tpc-purchase-panel]').forEach((candidate) => {
      const previousTimer = highlightTimers.get(candidate);
      if (previousTimer) window.clearTimeout(previousTimer);
      highlightTimers.delete(candidate);
      candidate.classList.remove('is-guiding');
    });

    void panel.offsetWidth;
    panel.classList.add('is-guiding');

    const duration = prefersReducedMotion() ? 1800 : HIGHLIGHT_DURATION_MS;
    const timer = window.setTimeout(() => {
      panel.classList.remove('is-guiding');
      highlightTimers.delete(panel);
    }, duration);

    highlightTimers.set(panel, timer);
  };

  const guideSelection = (root) => {
    const now = window.performance?.now?.() || Date.now();
    const previous = lastGuideTimes.get(root) || 0;
    if (now - previous < 120) return;
    lastGuideTimes.set(root, now);

    activateFocusFade(root);
    highlightActivePurchasePanel(root);

    window.requestAnimationFrame(() => {
      scrollActivePurchasePanelToTop(root);
    });
  };

  const getSantiagoDateParts = () => {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Santiago',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric'
    }).formatToParts(new Date());

    return parts.reduce((result, part) => {
      if (part.type !== 'literal') result[part.type] = Number(part.value);
      return result;
    }, {});
  };

  const makeUtcDate = (year, monthIndex, day) => new Date(Date.UTC(year, monthIndex, day, 12));

  const shiftMonth = (year, monthIndex, delta) => {
    const shifted = makeUtcDate(year, monthIndex + delta, 1);
    return {
      year: shifted.getUTCFullYear(),
      monthIndex: shifted.getUTCMonth()
    };
  };

  const formatCycleDate = (date) => new Intl.DateTimeFormat('es-CL', {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC'
  }).format(date);

  const formatCycleMonth = (date) => new Intl.DateTimeFormat('es-CL', {
    month: 'long',
    timeZone: 'UTC'
  }).format(date);

  const toIsoDate = (date) => date.toISOString().slice(0, 10);

  const getCycleDates = () => {
    const { year, month, day } = getSantiagoDateParts();
    const currentMonthIndex = month - 1;

    // El ciclo permanece abierto hasta el final del día 2 en Santiago.
    // A partir del día 3, las compras pasan al despacho del mes siguiente.
    const dispatchMonth = day >= 3
      ? shiftMonth(year, currentMonthIndex, 1)
      : { year, monthIndex: currentMonthIndex };

    const openingMonth = shiftMonth(dispatchMonth.year, dispatchMonth.monthIndex, -1);
    const followingMonth = shiftMonth(dispatchMonth.year, dispatchMonth.monthIndex, 1);

    return {
      dispatch: makeUtcDate(dispatchMonth.year, dispatchMonth.monthIndex, 10),
      close: makeUtcDate(dispatchMonth.year, dispatchMonth.monthIndex, 2),
      open: makeUtcDate(openingMonth.year, openingMonth.monthIndex, 3),
      nextOpen: makeUtcDate(dispatchMonth.year, dispatchMonth.monthIndex, 3),
      followingDispatch: makeUtcDate(followingMonth.year, followingMonth.monthIndex, 10)
    };
  };

  const updateDateElements = (root, selector, date, label) => {
    root.querySelectorAll(selector).forEach((element) => {
      element.textContent = label;
      if (element.tagName === 'TIME') {
        element.setAttribute('datetime', toIsoDate(date));
      }
    });
  };

  const updateDynamicCycleDates = (root) => {
    const dates = getCycleDates();
    const labels = {
      dispatch: formatCycleDate(dates.dispatch),
      close: formatCycleDate(dates.close),
      open: formatCycleDate(dates.open),
      nextOpen: formatCycleDate(dates.nextOpen),
      followingDispatch: formatCycleDate(dates.followingDispatch),
      editionMonth: formatCycleMonth(dates.dispatch),
      followingEditionMonth: formatCycleMonth(dates.followingDispatch)
    };

    updateDateElements(root, '[data-tpc-next-dispatch]', dates.dispatch, labels.dispatch);
    updateDateElements(root, '[data-tpc-cycle-close]', dates.close, labels.close);
    updateDateElements(root, '[data-tpc-cycle-open]', dates.open, labels.open);
    updateDateElements(root, '[data-tpc-next-cycle-open]', dates.nextOpen, labels.nextOpen);
    updateDateElements(root, '[data-tpc-following-dispatch]', dates.followingDispatch, labels.followingDispatch);

    root.querySelectorAll('[data-tpc-edition-month]').forEach((element) => {
      element.textContent = labels.editionMonth;
    });

    root.querySelectorAll('[data-tpc-following-edition-month]').forEach((element) => {
      element.textContent = labels.followingEditionMonth;
    });

    root.querySelectorAll('[data-tpc-dispatch-input]').forEach((input) => {
      input.value = labels.dispatch;
    });
  };

  const setPanelVisibility = (root, activePanel) => {
    root.querySelectorAll('[data-tpc-purchase-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.tpcPurchasePanel !== activePanel;
    });
  };

  const updateUrl = (variantId) => {
    if (!variantId || !window.history?.replaceState) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({ variantId }, '', url);
  };

  const selectPlan = (root, radio, { updateAddress = false, guide = false } = {}) => {
    if (!radio || radio.disabled) return;

    const plan = radio.dataset.plan;
    const variantId = radio.dataset.variantId;
    const title = radio.dataset.title || '';
    const price = radio.dataset.price || '';
    const available = radio.dataset.available !== 'false';

    root.querySelectorAll('[data-tpc-plan-card]').forEach((card) => {
      const cardRadio = card.querySelector('[data-tpc-plan-radio]');
      const selected = cardRadio === radio;
      card.classList.toggle('is-selected', selected);
      card.setAttribute('aria-checked', selected ? 'true' : 'false');
    });

    radio.checked = true;
    root.dataset.selectedPlan = plan;
    root.dataset.selectedVariantId = variantId || '';

    if (plan === 'monthly') {
      setPanelVisibility(root, 'monthly');
    } else {
      setPanelVisibility(root, 'prepaid');

      const variantInput = root.querySelector('[data-tpc-cart-variant]');
      const planInput = root.querySelector('[data-tpc-cart-plan]');
      const summaryTitle = root.querySelector('[data-tpc-selected-title]');
      const summaryPrice = root.querySelector('[data-tpc-selected-price]');
      const addButton = root.querySelector('[data-tpc-add-button]');

      if (variantInput) variantInput.value = variantId;
      if (planInput) planInput.value = title;
      if (summaryTitle) summaryTitle.textContent = title;
      if (summaryPrice) summaryPrice.textContent = price;
      if (addButton) {
        addButton.disabled = !available;
        addButton.textContent = available
          ? `Agregar ${title} al carrito`
          : `${title} agotado`;
      }
    }

    const mobileButton = root.querySelector('[data-tpc-mobile-cta]');
    if (mobileButton) {
      mobileButton.textContent = plan === 'monthly'
        ? 'Completar datos de suscripción'
        : `Continuar con ${title}`;
      mobileButton.disabled = plan !== 'monthly' && !available;
    }

    if (updateAddress) updateUrl(variantId);
    if (guide) guideSelection(root);
  };

  const initRoot = (root) => {
    if (root.dataset.tpcInitialized === 'true') return;
    root.dataset.tpcInitialized = 'true';

    // Liquid ya entrega las fechas en el HTML inicial. Esta llamada funciona como
    // corrección en el navegador frente a caché y asegura la zona America/Santiago.
    updateDynamicCycleDates(root);

    const radios = Array.from(root.querySelectorAll('[data-tpc-plan-radio]'));
    const initialRadio = radios.find((radio) => radio.checked && !radio.disabled)
      || radios.find((radio) => !radio.disabled);

    selectPlan(root, initialRadio);

    radios.forEach((radio) => {
      const activate = () => {
        selectPlan(root, radio, { updateAddress: true, guide: true });
      };

      radio.addEventListener('click', activate);
      radio.addEventListener('change', activate);
    });

    const purchaseArea = root.querySelector('[data-tpc-purchase]');
    const mobileButton = root.querySelector('[data-tpc-mobile-cta]');
    mobileButton?.addEventListener('click', () => {
      purchaseArea?.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'start'
      });
      window.setTimeout(
        () => highlightActivePurchasePanel(root),
        prefersReducedMotion() ? 0 : 420
      );
      const activePanel = root.querySelector('[data-tpc-purchase-panel]:not([hidden])');
      const focusable = activePanel?.querySelector('input, button, select, textarea, a[href]');
      window.setTimeout(() => focusable?.focus({ preventScroll: true }), 450);
    });

    const productForm = root.querySelector('.tpc-product-form');
    productForm?.addEventListener('submit', (event) => {
      const variantInput = productForm.querySelector('[data-tpc-cart-variant]');
      const allowedValue = productForm.querySelector('[data-tpc-allowed-variant-ids]')?.value || '';
      const allowedIds = allowedValue
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);

      if (!variantInput?.value || !allowedIds.includes(variantInput.value)) {
        event.preventDefault();
        window.alert('Selecciona un pack prepago válido antes de agregarlo al carrito.');
      }
    });
  };

  const initAll = (container = document) => {
    container.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
